/**
 * Контракты режимов «Скорость» и «Точность» (ТЗ §6, режимы 6 и 7).
 *
 * Материал у режимов тот же, что у обычной тренировки, — частотные слова.
 * Различаются правила: «Скорость» поднимает целевой темп ступенями и требует
 * держать его без потери точности, «Точность» засчитывает только прогон без
 * единой ошибки и возвращает к началу элемента, пока он не набран чисто.
 */

import type {
  HintLevel,
  KeystrokeEvent,
  LanguageCode,
  LayoutId,
  MasteryStat,
  MasteryUnit,
  TrainingMode,
  TypingError,
  TypingMetrics,
} from '@type2x/shared-contracts';
import type { RunObservation } from '@type2x/typing-engine';

/** Режимы, которые ведёт эта функция. */
export const TEMPO_MODES = ['speed', 'accuracy'] as const;
export type TempoMode = (typeof TEMPO_MODES)[number];

/**
 * Режимы функции — подмножество режимов обучения.
 *
 * Проверка на уровне типов: итог сессии уходит на сервер с этим значением, и
 * режим, которого нет в общем перечне, не был бы учтён в разнообразии.
 */
const MODES_ARE_TRAINING_MODES: readonly TrainingMode[] = TEMPO_MODES;
void MODES_ARE_TRAINING_MODES;

/** Снимок навыка, из которого собирается занятие. */
export interface TempoSnapshot {
  readonly language: LanguageCode;
  readonly layoutId: LayoutId;
  readonly units: Readonly<Record<string, MasteryUnit>>;
  readonly stats: Readonly<Record<string, MasteryStat>>;
  /** Скорость первой диагностики, CPM; 0 — baseline ещё не измерен. */
  readonly baselineCpm: number;
  /** Ступень темпа, сохранённая в профиле; null — режим ещё не начинали. */
  readonly targetCpm: number | null;
}

/** Прогон одного элемента занятия. */
export interface TempoRun {
  readonly metrics: TypingMetrics;
  readonly events: readonly KeystrokeEvent[];
  readonly errors: readonly TypingError[];
}

/** Элемент занятия: строка, которую пользователь набирает за один прогон. */
export interface TempoDrill {
  readonly words: readonly string[];
  /** Текст прогона: слова, соединённые пробелами. */
  readonly text: string;
  /** Сколько раз элемент показывался за занятие. */
  readonly attempts: number;
  /** Элемент взят: темп удержан либо строка набрана чисто. */
  readonly cleared: boolean;
  /** Попытки исчерпаны: элемент отложен до следующего занятия. */
  readonly deferred: boolean;
}

/**
 * Чем закончился последний прогон.
 *
 * Пользователю нужно понимать, почему ступень сдвинулась или почему элемент
 * повторяется: непрозрачный тренажёр не объясняет, что происходит.
 */
export const TEMPO_VERDICTS = [
  /** Темп взят, ступень пока держится. */
  'held',
  /** Темп удержан нужное число раз: ступень поднялась. */
  'advanced',
  /** Точность в порядке, но до цели не хватило скорости. */
  'slow',
  /** Точность просела: ступень опустилась. */
  'dropped',
  /** Строка набрана без единой ошибки. */
  'clean',
  /** Ошибка: элемент повторяется с начала. */
  'retry',
  /** Попытки исчерпаны: элемент отложен. */
  'deferred',
] as const;
export type TempoVerdict = (typeof TEMPO_VERDICTS)[number];

/** Занятие режима. */
export interface TempoSession {
  readonly mode: TempoMode;
  readonly language: LanguageCode;
  readonly layoutId: LayoutId;
  /**
   * Ступень лестницы темпа, CPM.
   *
   * Двигает её только режим «Скорость»; режим «Точность» ступень уважает, но
   * не меняет — чистота набирается не разгоном.
   */
  readonly ladderCpm: number;
  /**
   * Ориентир прогона, CPM.
   *
   * В режиме «Скорость» это сама ступень — цель, которую нужно взять; в
   * режиме «Точность» — спокойный темп, выше которого чистого прохода ждать
   * не стоит.
   */
  readonly targetCpm: number;
  /** Сколько прогонов подряд темп удержан на этой ступени. */
  readonly holds: number;
  readonly drills: readonly TempoDrill[];
  readonly currentIndex: number;
  readonly finished: boolean;
  readonly hintLevel: HintLevel;
  /** Суммарные метрики набора за занятие. */
  readonly metrics: TypingMetrics;
  /** Ошибки всех прогонов: из них собирается сводка по типам для сервера. */
  readonly errors: readonly TypingError[];
  /** Накопленные наблюдения по словам занятия. */
  readonly observed: Readonly<Record<string, RunObservation>>;
  /** Чем закончился последний прогон; null — прогонов ещё не было. */
  readonly lastVerdict: TempoVerdict | null;
}

/** Сколько прогонов в занятии режима «Скорость». */
export const SPEED_RUNS = 5;

/** Сколько слов в одном прогоне режима «Скорость». */
export const WORDS_PER_RUN = 6;

/** Сколько элементов в занятии режима «Точность». */
export const ACCURACY_DRILLS = 8;

/** Сколько слов в одном элементе режима «Точность». */
export const ACCURACY_WORDS_PER_DRILL = 2;

/** Шаг лестницы темпа, CPM. */
export const STEP_CPM = 15;

/** Сколько раз подряд нужно взять темп, чтобы ступень поднялась. */
export const HOLDS_TO_ADVANCE = 2;

/** Ниже этой ступени лестница не опускается, CPM. */
export const MIN_TARGET_CPM = 60;

/**
 * Точность, ниже которой темп не засчитывается.
 *
 * Значение выбрано по фактическому объёму прогона: строка из шести частотных
 * слов — это около сорока символов, одна ошибка в ней даёт точность 0.975 и
 * проходит, две дают 0.95 и уже нет. Скорость, купленная ошибками, — не
 * скорость (ТЗ §7.7).
 */
export const SPEED_MIN_ACCURACY = 0.96;

/** Сколько раз элемент режима «Точность» повторяется, прежде чем отложится. */
export const MAX_ATTEMPTS_PER_DRILL = 3;

/**
 * Доля ступени, которую режим «Точность» показывает как спокойный темп.
 *
 * Чистая печать — это медленная печать: гнаться за целью скорости здесь не за
 * чем (ТЗ §6, режим 7).
 */
export const ACCURACY_PACE_RATIO = 0.8;

/** Уровень подсказок режима. */
export const HINT_LEVEL: Readonly<Record<TempoMode, HintLevel>> = {
  // Скорость тренируется без клавиатуры на экране: взгляд на неё и есть то,
  // что мешает разогнаться.
  speed: 'no-hints',
  // Точность требует времени на поиск клавиши, поэтому подсказка появляется
  // с задержкой, а не сразу.
  accuracy: 'delayed-hint',
};
